const pollingIntervals = {}

const state = {
  sessions: {}
}

const mutations = {
  setSession (state, { applicantId, session }) {
    state.sessions = {
      ...state.sessions,
      [applicantId]: session
    }
  },

  updateSessionStatus (state, { applicantId, status, progress, error, booster_reasoning, matched_job_titles, last_role }) {
    const existing = state.sessions[applicantId]
    if (!existing) return
    state.sessions = {
      ...state.sessions,
      [applicantId]: {
        ...existing,
        status,
        progress: progress || existing.progress,
        error: error || existing.error,
        booster_reasoning: booster_reasoning || existing.booster_reasoning,
        matched_job_titles: matched_job_titles || existing.matched_job_titles,
        last_role: last_role || existing.last_role
      }
    }
  },

  setScoredItems (state, { applicantId, items, total_ranked, booster_reasoning, matched_job_titles }) {
    const existing = state.sessions[applicantId]
    if (!existing) return
    state.sessions = {
      ...state.sessions,
      [applicantId]: {
        ...existing,
        scoredItems: items,
        total_ranked,
        booster_reasoning: booster_reasoning || existing.booster_reasoning,
        matched_job_titles: matched_job_titles || existing.matched_job_titles
      }
    }
  },

  setResults (state, { applicantId, page, items, has_more, total_ranked, booster_reasoning, matched_job_titles }) {
    const existing = state.sessions[applicantId]
    if (!existing) return
    const pages = { ...(existing.pages || {}) }
    pages[page] = items
    state.sessions = {
      ...state.sessions,
      [applicantId]: {
        ...existing,
        pages,
        has_more,
        total_ranked,
        booster_reasoning: booster_reasoning || existing.booster_reasoning,
        matched_job_titles: matched_job_titles || existing.matched_job_titles
      }
    }
  },

  setAiResult (state, { applicantId, offerId, result }) {
    const existing = state.sessions[applicantId]
    if (!existing) return
    const aiResults = { ...(existing.aiResults || {}) }
    aiResults[offerId] = result
    state.sessions = {
      ...state.sessions,
      [applicantId]: {
        ...existing,
        aiResults
      }
    }
  },

  setAiLoading (state, { applicantId, offerId, loading }) {
    const existing = state.sessions[applicantId]
    if (!existing) return
    const aiLoading = { ...(existing.aiLoading || {}) }
    if (loading) {
      aiLoading[offerId] = true
    } else {
      delete aiLoading[offerId]
    }
    state.sessions = {
      ...state.sessions,
      [applicantId]: {
        ...existing,
        aiLoading
      }
    }
  },

  clearSession (state, applicantId) {
    const newSessions = { ...state.sessions }
    delete newSessions[applicantId]
    state.sessions = newSessions
  }
}

const actions = {
  async startRecommendation ({ commit, dispatch }, { applicantId, vm }) {
    try {
      const response = await vm.$caller.request('post', '/start_position_recommendation', 'admin_headers', {
        applicant_id: applicantId
      })

      commit('setSession', {
        applicantId,
        session: {
          session_id: response.session_id,
          status: response.status,
          progress: { scored: 0, total: 0 },
          scoredItems: [],
          pages: {},
          error: null,
          has_more: false,
          total_ranked: 0,
          booster_reasoning: null,
          matched_job_titles: [],
          aiResults: {},
          aiLoading: {}
        }
      })

      dispatch('pollStatus', { applicantId, sessionId: response.session_id, vm })
      return response
    } catch (err) {
      console.error('[PositionReco] Start failed:', err.message)
      commit('setSession', {
        applicantId,
        session: {
          session_id: null,
          status: 'failed',
          progress: { scored: 0, total: 0 },
          scoredItems: [],
          pages: {},
          error: err.response ? err.response.data.error : err.message,
          has_more: false,
          total_ranked: 0,
          booster_reasoning: null,
          matched_job_titles: [],
          aiResults: {},
          aiLoading: {}
        }
      })
      throw err
    }
  },

  pollStatus ({ commit, dispatch, state }, { applicantId, sessionId, vm }) {
    // Clear existing polling for this applicant
    if (pollingIntervals[applicantId]) {
      clearInterval(pollingIntervals[applicantId])
    }

    pollingIntervals[applicantId] = setInterval(async () => {
      try {
        const response = await vm.$caller.request('get', '/recommendation_status', 'admin_headers', {
          session_id: sessionId
        })

        commit('updateSessionStatus', {
          applicantId,
          status: response.status,
          progress: response.progress,
          error: response.error,
          booster_reasoning: response.booster_reasoning,
          matched_job_titles: response.matched_job_titles,
          last_role: response.last_role
        })

        if (response.status === 'scored') {
          // Scoring done - fetch all scored items and stop polling
          clearInterval(pollingIntervals[applicantId])
          delete pollingIntervals[applicantId]
          dispatch('fetchScoredItems', { applicantId, sessionId, vm })
        } else if (response.status === 'ready') {
          // AI evaluation done - fetch results for the evaluated page
          clearInterval(pollingIntervals[applicantId])
          delete pollingIntervals[applicantId]
          const page = response.evaluating_page || 1
          dispatch('fetchResults', { applicantId, sessionId, page, vm })
        } else if (response.status === 'failed') {
          clearInterval(pollingIntervals[applicantId])
          delete pollingIntervals[applicantId]
        }
      } catch (err) {
        console.error('[PositionReco] Poll error:', err.message)
        clearInterval(pollingIntervals[applicantId])
        delete pollingIntervals[applicantId]
      }
    }, 3000)
  },

  async fetchScoredItems ({ commit }, { applicantId, sessionId, vm }) {
    try {
      const response = await vm.$caller.request('get', '/recommendation_scored_items', 'admin_headers', {
        session_id: sessionId
      })

      commit('setScoredItems', {
        applicantId,
        items: response.items,
        total_ranked: response.total_ranked,
        booster_reasoning: response.booster_reasoning,
        matched_job_titles: response.matched_job_titles
      })

      commit('updateSessionStatus', {
        applicantId,
        status: response.status
      })

      return response
    } catch (err) {
      console.error('[PositionReco] Fetch scored items error:', err.message)
      throw err
    }
  },

  async fetchResults ({ commit, state }, { applicantId, sessionId, page, vm }) {
    try {
      const response = await vm.$caller.request('get', '/recommendation_results', 'admin_headers', {
        session_id: sessionId,
        page: page
      })

      commit('setResults', {
        applicantId,
        page: response.page,
        items: response.items,
        has_more: response.has_more,
        total_ranked: response.total_ranked,
        booster_reasoning: response.booster_reasoning,
        matched_job_titles: response.matched_job_titles
      })

      commit('updateSessionStatus', {
        applicantId,
        status: response.status
      })

      return response
    } catch (err) {
      console.error('[PositionReco] Fetch results error:', err.message)
      throw err
    }
  },

  async calculateMore ({ commit, dispatch, state }, { applicantId, vm }) {
    const session = state.sessions[applicantId]
    if (!session) return

    try {
      const response = await vm.$caller.request('post', '/calculate_more_recommendations', 'admin_headers', {
        session_id: session.session_id
      })

      commit('updateSessionStatus', {
        applicantId,
        status: 'evaluating'
      })

      dispatch('pollStatus', { applicantId, sessionId: session.session_id, vm })
      return response
    } catch (err) {
      console.error('[PositionReco] Calculate more error:', err.message)
      throw err
    }
  },

  async evaluateOffer ({ commit, state }, { applicantId, offerId, vm }) {
    const session = state.sessions[applicantId]
    if (!session) return

    commit('setAiLoading', { applicantId, offerId, loading: true })

    try {
      const response = await vm.$caller.request('post', '/evaluate_single_recommendation', 'admin_headers', {
        session_id: session.session_id,
        offer_id: offerId
      })

      commit('setAiResult', { applicantId, offerId, result: response })
      return response
    } catch (err) {
      console.error('[PositionReco] Evaluate offer error:', err.message)
      commit('setAiResult', {
        applicantId,
        offerId,
        result: {
          offer_id: offerId,
          ba_decision: 'error',
          ba_reasoning: err.message,
          ba_proposer: null,
          ba_evaluation_id: null
        }
      })
      throw err
    } finally {
      commit('setAiLoading', { applicantId, offerId, loading: false })
    }
  },

  stopPolling ({ state }, { applicantId }) {
    if (pollingIntervals[applicantId]) {
      clearInterval(pollingIntervals[applicantId])
      delete pollingIntervals[applicantId]
    }
  }
}

const getters = {
  getSession: (state) => (applicantId) => {
    return state.sessions[applicantId] || null
  },

  getScoredItems: (state) => (applicantId) => {
    const session = state.sessions[applicantId]
    if (!session || !session.scoredItems) return []
    return session.scoredItems
  },

  getAllResults: (state) => (applicantId) => {
    const session = state.sessions[applicantId]
    if (!session || !session.pages) return []
    const allItems = []
    const sortedPages = Object.keys(session.pages).sort((a, b) => a - b)
    sortedPages.forEach(page => {
      allItems.push(...(session.pages[page] || []))
    })
    return allItems
  },

  getResultsByPage: (state) => (applicantId) => {
    const session = state.sessions[applicantId]
    if (!session || !session.pages) return []
    const order = { green: 0, orange: 1, red: 2, error: 3 }
    const sortedPages = Object.keys(session.pages).sort((a, b) => a - b)
    return sortedPages.map(page => {
      const items = [...(session.pages[page] || [])]
      items.sort((a, b) => {
        const aOrder = order[a.ba_decision] !== undefined ? order[a.ba_decision] : 9
        const bOrder = order[b.ba_decision] !== undefined ? order[b.ba_decision] : 9
        if (aOrder !== bOrder) return aOrder - bOrder
        return (b.score || 0) - (a.score || 0)
      })
      return { page: parseInt(page), items }
    })
  },

  getAiResults: (state) => (applicantId) => {
    const session = state.sessions[applicantId]
    if (!session || !session.aiResults) return {}
    return session.aiResults
  },

  isOfferAiLoading: (state) => (applicantId, offerId) => {
    const session = state.sessions[applicantId]
    if (!session || !session.aiLoading) return false
    return !!session.aiLoading[offerId]
  },

  isActive: (state) => (applicantId) => {
    const session = state.sessions[applicantId]
    if (!session) return false
    return !['ready', 'failed', 'scored'].includes(session.status)
  }
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
}
